import { useEffect, useState } from "react";

import { Box, Grid, Paper, TextField, Typography, Alert } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";

import API from "api";

import { styles } from "./styles";

const AppSettings = () => {
  const [currentVersion, setCurrentVersion] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);

  const fetchCurrentVersion = async () => {
    try {
      setFetching(true);
      const response = await API.App.getMinimumSupportedVersion();
      setCurrentVersion(response.data.minimumSupportedVersion);
    } catch (error) {
      toast.error("Failed to fetch current minimum supported version");
    } finally {
      setFetching(false);
    }
  };

  useEffect(() => {
    fetchCurrentVersion();
  }, []);

  const formik = useFormik({
    initialValues: {
      version: currentVersion || "",
    },
    enableReinitialize: true,
    validationSchema: Yup.object({
      version: Yup.string()
        .required("Version is required")
        .matches(/^\d+\.\d+\.\d+$/, "Version must be in format X.Y.Z (e.g., 2.1.3)"),
    }),
    onSubmit: async (values) => {
      setLoading(true);
      try {
        await API.Staff.updateMinimumSupportedMobileVersion(values.version);
        toast.success("Minimum supported version updated successfully");
        await fetchCurrentVersion();
      } catch (error) {
        toast.error(error?.response?.data?.message || "Failed to update version");
      } finally {
        setLoading(false);
      }
    },
  });

  const isNewVersionLower = () => {
    if (!formik.values.version || !currentVersion) return false;
    const currentParts = currentVersion.split(".").map(Number);
    const newParts = formik.values.version.split(".").map(Number);
    
    for (let i = 0; i < 3; i++) {
      if (newParts[i] < currentParts[i]) return true;
      if (newParts[i] > currentParts[i]) return false;
    }
    return false;
  };

  if (fetching) {
    return (
      <Box sx={styles.container}>
        <Typography variant="h6" color="text.secondary">
          Loading...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={styles.container}>
      <Grid container>
        <Grid xs={12} md={8} lg={6} item>
          <Box sx={styles.paper}>
            <Typography
              variant="h6"
              fontSize="1rem"
              color="text.secondary"
              mb={2}
              fontWeight={600}
            >
              App Settings
            </Typography>

            <Box mb={4}>
              <Typography variant="body2" color="text.secondary" mb={1} fontWeight={600}>
                Current Minimum Supported Version
              </Typography>
              <Box sx={styles.versionDisplay}>
                {currentVersion}
              </Box>
            </Box>

            <Box mb={4}>
              <Typography variant="body2" color="text.secondary" mb={1} fontWeight={600}>
                Update Minimum Supported Version
              </Typography>
              <form onSubmit={formik.handleSubmit}>
                <TextField
                  id="version"
                  fullWidth
                  label="Version (e.g., 2.1.3)"
                  variant="outlined"
                  size="small"
                  value={formik.values.version}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.version &&
                    typeof formik.errors.version === "string" &&
                    formik.errors.version !== ""
                  }
                  helperText={
                    formik.touched.version &&
                    typeof formik.errors.version === "string" &&
                    formik.errors.version !== ""
                      ? formik.errors.version
                      : " "
                  }
                  sx={styles.textField}
                />

                {isNewVersionLower() && (
                  <Alert severity="warning" sx={styles.warning}>
                    Warning: The version you entered is lower than the current minimum supported version ({currentVersion}). This may cause users with older versions to be unable to use the app.
                  </Alert>
                )}

                <LoadingButton
                  variant="contained"
                  sx={styles.button}
                  type="submit"
                  loading={loading}
                  disabled={!formik.isValid || !formik.dirty}
                >
                  Update Version
                </LoadingButton>
              </form>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AppSettings;
